import { useEffect, useState } from "react";
import axios from "axios";
import ListenerGlobe from "@/components/ListenerGlobe";
import { trackVisit } from "@/lib/visitorSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live FlagCounter widget. Counter ID: z3oe (transferred from
 * digitalrevolutionradio.com original site).
 *
 * The widget URL controls the look:
 *   bg_      background hex
 *   txt_     text hex
 *   border_  border hex
 *   columns_ flag columns
 *   maxflags max flags shown
 *   labels_  show country labels
 *   pageviews_ show pageview count
 *   flags_   show flag icons
 *   percent_ show percentages
 *
 * Full breakdown for visitors is on https://s01.flagcounter.com/more/z3oe/
 */
const FLAG_COUNTER_ID = "z3oe";
const FLAG_COUNTER_IMG = `https://s01.flagcounter.com/count2/${FLAG_COUNTER_ID}/bg_18181B/txt_FAFAFA/border_DC2626/columns_8/maxflags_64/viewers_0/labels_1/pageviews_1/flags_1/percent_0/`;
// US regions counter (count2_US endpoint) — separate widget for state-level
// breakdown. Same Counter ID, same z3oe stats pool (801 regions accumulated).
const REGION_COUNTER_IMG = `https://s01.flagcounter.com/count2_US/${FLAG_COUNTER_ID}/bg_18181B/txt_FAFAFA/border_DC2626/columns_4/maxflags_64/viewers_0/labels_1/pageviews_0/flags_1/percent_0/`;
// FlagCounter stats landing page. We deliberately bypass the
// `info.flagcounter.com/{id}` short URL because it 302-redirects to an
// HTTP (not HTTPS) version of the final page, which modern browsers block
// as mixed-content when the user clicks from our HTTPS site — making the
// click look like it "did nothing". Linking directly to the HTTPS final
// destination skips the broken redirect chain.
const FLAG_COUNTER_LINK = `https://s01.flagcounter.com/more/${FLAG_COUNTER_ID}/`;

export default function GlobalReach({ showGlobe = true }) {
  const [total, setTotal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await trackVisit();
        setTotal(data.total);
      } catch (e) {
        try {
          const res = await axios.get(`${API}/visits`);
          setTotal(res.data.total);
        } catch (e2) {
          /* silent */
        }
      }
    })();
  }, []);

  return (
    <section
      id="reach"
      className="relative border-b border-zinc-800 bg-zinc-950"
      data-testid="global-reach-section"
    >
      <div className="px-4 md:px-10 py-12 md:py-16">
        {/* Header */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
              //_GLOBAL_REACH
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter text-zinc-50 glitch">
              FROM YOUR <span className="text-red-600">GARAGE</span>
              <br className="hidden md:block" />{" "}
              <span className="md:inline">TO THE WHOLE </span>
              <span className="text-red-600">PLANET</span>.
            </h2>
          </div>
          <div className="text-sm text-zinc-300 max-w-sm">
            Digital Revolution Radio&apos;s signal reaches listeners across the
            globe. The counter below is live — every visitor adds a flag.
          </div>
        </div>

        {/* Visitor count stat block */}
        <div
          className="mt-8 grid grid-cols-12 gap-0 border border-zinc-800"
          data-testid="visitor-count-block"
        >
          <div className="col-span-12 md:col-span-5 p-6 border-b md:border-b-0 md:border-r border-zinc-800 bg-zinc-900/40">
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3 flex items-center gap-2">
              <span className="on-air-dot" aria-hidden />
              <span>//_TOTAL_VISITORS · LIVE</span>
            </div>
            <div
              className="font-display text-6xl md:text-7xl text-zinc-50 leading-none"
              data-testid="visit-count"
            >
              {total !== null ? total.toLocaleString() : "—"}
              <span className="text-red-600">+</span>
            </div>
            <div className="text-xs text-zinc-300 tracking-widest uppercase mt-3">
              SINCE LAUNCH · COUNTING IN REAL TIME
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 p-6 flex flex-col justify-center">
            <div className="font-display text-2xl md:text-3xl text-zinc-100 leading-tight tracking-tight">
              ONE HUNDRED THOUSAND HEADS,
              <br />
              <span className="text-red-600">ONE SIGNAL</span>.
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mt-3 max-w-xl">
              That&apos;s the running tally of everyone who&apos;s wandered into Digital
              Revolution Radio since we flipped the transmitter on. Welcome
              to the family — you&apos;re visitor{" "}
              <span className="text-zinc-300">
                {total !== null ? total.toLocaleString() : "—"}
              </span>{" "}
              and counting.
            </p>
          </div>
        </div>

        {/* 3D rotating listener globe — hidden when caller sets showGlobe={false},
            e.g. /contest already shows a globe elsewhere. */}
        {showGlobe && (
          <div className="mt-8" data-testid="globe-wrapper">
            <ListenerGlobe />
          </div>
        )}

        {/* FlagCounter Widgets — countries + US regions side-by-side */}
        <div
          className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-0 border border-zinc-800"
          data-testid="flag-counter-wrapper"
        >
          {/* Countries */}
          <div className="p-4 md:p-6 bg-zinc-900/40 border-b lg:border-b-0 lg:border-r border-zinc-800">
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-4 flex items-center justify-between flex-wrap gap-2">
              <span>//_COUNTRIES · 150+ FLAGS</span>
              <a
                href={FLAG_COUNTER_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-500 hover:text-white transition-colors"
                data-testid="flag-counter-details-link"
              >
                FULL BREAKDOWN →
              </a>
            </div>
            <a
              href={FLAG_COUNTER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
              data-testid="flag-counter-link"
            >
              <img
                src={FLAG_COUNTER_IMG}
                alt="Flag Counter — Digital Revolution Radio listeners by country"
                className="max-w-full mx-auto block"
                data-testid="flag-counter-img"
              />
            </a>
          </div>

          {/* US Regions */}
          <div className="p-4 md:p-6 bg-zinc-900/40">
            <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-4 flex items-center justify-between flex-wrap gap-2">
              <span>//_US_REGIONS · 800+ STATES</span>
              <a
                href={FLAG_COUNTER_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-500 hover:text-white transition-colors"
                data-testid="region-counter-details-link"
              >
                FULL BREAKDOWN →
              </a>
            </div>
            <a
              href={FLAG_COUNTER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
              data-testid="region-counter-link"
            >
              <img
                src={REGION_COUNTER_IMG}
                alt="Region Counter — Digital Revolution Radio listeners by US state"
                className="max-w-full mx-auto block"
                data-testid="region-counter-img"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
